import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Zoom,
  Snackbar
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { TransitionProps } from '@mui/material/transitions';
import { useAppSelector } from 'app/hooks';
import React, { useCallback, useEffect, useState } from 'react';
import {
  MdCancel as CancelIcon,
  MdCheckCircle as CheckCircleIcon,
  MdDoneAll as DoneAllIcon,
  MdFilterList as FilterListIcon,
  MdHelpOutline as HelpOutlineIcon,
  MdHourglassEmpty as HourglassEmptyIcon,
  MdDelete,
  MdEdit,
  MdSummarize,
  MdMoreVert as MoreVertIcon,
  MdRestore as RestoreIcon,
  MdSearch as SearchIcon
} from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import axiosInstance from 'Services/APIService';
import { RootState } from 'store';
import {
  deleteEmployee,
  listScreenings,
  ListScreeningsPayload,
  resetScreeningsState,
  Screening,
  updateEmployeeStatus
} from '../../../slices/payrolslices/PeopleSlice';

interface PeopleListProps {
  viewMode: 'table';
  pageSize?: number;
  onAddDetails: (screening: Screening) => void;
  getFontSize: (small: string, medium: string, large: string) => string;
  isSmallScreen: boolean;
  isMediumScreen: boolean;
  setDetailsModalOpen: (open: boolean) => void;
  setSummaryModalOpen: (open: boolean) => void;
  setSelectedEmployee: (employee: Screening | null) => void;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Zoom ref={ref} {...props} />;
});

const PeopleList: React.FC<PeopleListProps> = ({
  viewMode,
  pageSize = 10,
  onAddDetails,
  getFontSize,
  isSmallScreen,
  isMediumScreen,
  setDetailsModalOpen,
  setSummaryModalOpen,
  setSelectedEmployee
}) => {
  const dispatch = useDispatch();
  const {
    screenings,
    nextToken,
    loadingScreenings,
    screeningsError,
    hasMoreScreenings,
    success,
    message,
    error
  } = useSelector((state: RootState) => state.people);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [competencyFilter, setCompetencyFilter] = useState<string>('');
  const [jobTypeFilter, setJobTypeFilter] = useState<string>('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeScreening, setActiveScreening] = useState<Screening | null>(null);
  const [avatarUrls, setAvatarUrls] = useState<{ [key: string]: string | null }>({});
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [restoreConfirmOpen, setRestoreConfirmOpen] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState<string | null>(null);

  const activeFilterCount = [statusFilter, competencyFilter, jobTypeFilter].filter(Boolean)?.length;
  const selectedProjectId = useAppSelector((state) => state.homeTilesServices.selectedProjectId);
  const selectedServiceTitle = useAppSelector(
    (state) => state.serviceSelected.selectedServiceTitle
  );

  const loginInfo = localStorage.getItem('user-details');
  const userDetails = loginInfo ? JSON.parse(loginInfo) : null;

  const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}') as {
    projectId?: string;
    projectName?: string;
    isAdmin?: boolean;
  };
  const isAdminLogin =
    selectedProject?.isAdmin === true &&
    userDetails?.role?.some((r: any) => r.label.toLowerCase() === 'admin');


  const statusMap = {
    Active: {
      label: 'Active',
      bgcolor: '#4caf50',
      color: '#fff',
      icon: CheckCircleIcon
    },
    Inactive: {
      label: 'Inactive',
      bgcolor: '#f44336',
      color: '#fff',
      icon: CancelIcon
    },
    Pending: {
      label: 'Pending',
      bgcolor: '#ff9800',
      color: '#fff',
      icon: HourglassEmptyIcon
    },
    Completed: {
      label: 'Completed',
      bgcolor: '#2196f3',
      color: '#fff',
      icon: DoneAllIcon
    },
    Removed: {
      label: 'Removed',
      bgcolor: '#9e9e9e',
      color: '#fff',
      icon: MdDelete
    }
  };

  const buildPayload = useCallback((): ListScreeningsPayload => {
    const payload: ListScreeningsPayload = {
      group_id: selectedProjectId || '',
      service_id: selectedServiceTitle || '',
      page_size: pageSize,
    };

    if (search.trim()) {
      payload.search = search.trim();
    }

    if (statusFilter) {
      if (statusFilter === 'Removed') {
        payload.isDelete = true;
      } else if (statusFilter === 'Active') {
        payload.status = true;
        payload.isDelete = false;
      } else if (statusFilter === 'Inactive') {
        payload.status = false;
        payload.isDelete = false;
      }
    }

    if (competencyFilter) {
      payload.competency = competencyFilter;
    }

    if (jobTypeFilter) {
      payload.employmentType = jobTypeFilter;
    }

    return payload;
  }, [
    selectedProjectId,
    selectedServiceTitle,
    pageSize,
    search,
    statusFilter,
    competencyFilter,
    jobTypeFilter
  ]);

  // Monitor error state for restore action
  useEffect(() => {
    if (error && error.includes('Failed to update employee status')) {
      setShowError(error);
    }
  }, [error]);

  // Monitor success for fallback refresh
  useEffect(() => {
    if (success && selectedProjectId && selectedServiceTitle) {
      setShowSuccess(true);
      setTimeout(() => {
        dispatch(resetScreeningsState());
        const payload = buildPayload();
        dispatch(listScreenings(payload));
      }, 100); // Slight delay to ensure API updates propagate
    }
  }, [success, selectedProjectId, selectedServiceTitle, dispatch, buildPayload]);

  useEffect(() => {
    setFiltersApplied(activeFilterCount > 0);
  }, [activeFilterCount]);

  useEffect(() => {
    if (selectedProjectId && selectedServiceTitle && !initialFetchDone) {
      dispatch(resetScreeningsState());
      const initialPayload = buildPayload();
      dispatch(listScreenings(initialPayload));
      setInitialFetchDone(true);
    }
  }, [dispatch, selectedProjectId, selectedServiceTitle, initialFetchDone]);

  useEffect(() => {
    if (initialFetchDone && !isSmallScreen) {
      const timer = setTimeout(() => {
        handleSearchClick();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [statusFilter, competencyFilter, jobTypeFilter, initialFetchDone, isSmallScreen]);

  useEffect(() => {
    const fetchAvatars = async () => {
      const newAvatarUrls: { [key: string]: string | null } = {};
      for (const screening of screenings) {
        if (screening.avatar) {
          try {
            const response = await axiosInstance.get(
              `/avatar/get-avatar?path=${encodeURIComponent(screening.avatar)}`,
              { responseType: 'blob' }
            );
            const imageUrl = URL.createObjectURL(response.data);
            newAvatarUrls[screening.employeeId] = imageUrl;
          } catch (error) {
            console.error(`Error fetching avatar for ${screening.employeeName}:`, error);
            newAvatarUrls[screening.employeeId] = null;
          }
        } else {
          newAvatarUrls[screening.employeeId] = null;
        }
      }
      setAvatarUrls(newAvatarUrls);
    };

    if (screenings?.length > 0) fetchAvatars();

    return () => {
      Object.values(avatarUrls).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [screenings]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearchClick();
    }
  };

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    setStatusFilter(event.target.value);
    if (!isSmallScreen) {
      setTimeout(() => handleSearchClick(), 50);
    }
  };

  const handleCompetencyChange = (event: SelectChangeEvent<string>) => {
    setCompetencyFilter(event.target.value);
    if (!isSmallScreen) {
      setTimeout(() => handleSearchClick(), 50);
    }
  };

  const handleJobTypeChange = (event: SelectChangeEvent<string>) => {
    setJobTypeFilter(event.target.value);
    if (!isSmallScreen) {
      setTimeout(() => handleSearchClick(), 50);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, screening: Screening) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setActiveScreening(screening);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setActiveScreening(null);
  };

  const handleViewDetails = () => {
    if (activeScreening) {
      setSelectedEmployee(activeScreening);
      setSummaryModalOpen(true);
    }
    handleMenuClose();
  };

  const handleEditScreening = () => {
    if (activeScreening) {
      onAddDetails(activeScreening);
      setDetailsModalOpen(true);
    }
    handleMenuClose();
  };

  const handleRemoveScreening = () => {
    if (activeScreening) {
      setDeleteConfirmOpen(true);
    }
  };

  const handleRestoreScreening = () => {
    if (activeScreening) {
      setRestoreConfirmOpen(true);
    }
  };

  const handleConfirmDelete = () => {
    if (activeScreening && selectedProjectId && selectedServiceTitle) {
      dispatch(
        deleteEmployee({
          group_id: selectedProjectId,
          service_id: selectedServiceTitle,
          email_key: activeScreening.email,
          employee_id: activeScreening.employeeId
        })
      );
    }
    setDeleteConfirmOpen(false);
    handleMenuClose();
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    handleMenuClose();
  };

  const handleConfirmRestore = () => {
    if (activeScreening && selectedProjectId && selectedServiceTitle) {
      dispatch(
        updateEmployeeStatus({
          group_id: selectedProjectId,
          service_id: selectedServiceTitle,
          email_key: activeScreening.email,
          employee_id: activeScreening.employeeId,
          status: 'restore'
        } as any)
      );
      // Immediately trigger list refresh
      setTimeout(() => {
        dispatch(resetScreeningsState());
        const payload = buildPayload();
        dispatch(listScreenings(payload));
      }, 500); // Delay to allow API to update
    }
    setRestoreConfirmOpen(false);
    handleMenuClose();
  };

  const handleCancelRestore = () => {
    setRestoreConfirmOpen(false);
    handleMenuClose();
  };

  const handleFilterDialogOpen = () => setFilterDialogOpen(true);

  const handleFilterDialogClose = () => setFilterDialogOpen(false);

  const handleApplyFilters = () => {
    handleSearchClick();
    handleFilterDialogClose();
  };

  const handleResetFilters = () => {
    setStatusFilter('');
    setCompetencyFilter('');
    setJobTypeFilter('');
    setSearch('');
    setTimeout(() => handleSearchClick(), 100);
  };

  const handleSearchClick = useCallback(() => {
    if (!selectedProjectId || !selectedServiceTitle) {
      console.warn('Missing required IDs for search');
      return;
    }

    try {
      setSearchLoading(true);
      dispatch(resetScreeningsState());
      const payload = buildPayload();
      dispatch(listScreenings(payload));
      setSearchLoading(false);
    } catch (error) {
      console.error('Error in search:', error);
      setSearchLoading(false);
    }
  }, [dispatch, selectedProjectId, selectedServiceTitle, buildPayload]);

  const handleLoadMore = useCallback(() => {
    if (!hasMoreScreenings || loadingScreenings || !nextToken) return;

    const payload: ListScreeningsPayload = {
      ...buildPayload(),
      next_token: nextToken
    };

    dispatch(listScreenings(payload));
  }, [dispatch, buildPayload, nextToken, hasMoreScreenings, loadingScreenings]);

  const getCompetencyColor = (competency: string) => {
    switch (competency.toLowerCase()) {
      case 'competent':
        return 'success';
      case 'incompetent':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusDisplay = (screening: Screening) => {
    if (screening?.isDelete || screening?.isDeleted) {
      return 'Removed';
    }
    return screening.status ? 'Active' : 'Inactive';
  };

  const isRecordDeleted = (screening: Screening) => {
    return screening?.isDelete === true || screening?.isDeleted === true;
  };

  const renderFilters = () => (
    <Box sx={{ mb: isSmallScreen ? 1 : isMediumScreen ? 1.5 : 2 }}>
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={isSmallScreen ? 10 : 12} sm={3}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name or email"
              value={search}
              onChange={handleSearchChange}
              onKeyPress={handleSearchKeyPress}
              sx={{
                '& .MuiInputBase-root': {
                  height: isSmallScreen ? 32 : 36,
                  fontSize: getFontSize('0.75rem', '0.8125rem', '0.875rem')
                }
              }}
              InputProps={{
                endAdornment: (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {search && (
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSearch('');
                          if (!isSmallScreen) {
                            setTimeout(() => handleSearchClick(), 50);
                          }
                        }}
                        sx={{ p: 0.25 }}
                      >
                        <CancelIcon size={16} />
                      </IconButton>
                    )}
                    {searchLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <Tooltip title="Search" arrow TransitionComponent={Zoom}>
                        <IconButton
                          size="small"
                          onClick={handleSearchClick}
                          sx={{ p: isSmallScreen ? 0.5 : 0.75 }}
                        >
                          <SearchIcon size={isSmallScreen ? 18 : 20} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                )
              }}
            />
          </Box>
        </Grid>
        {isSmallScreen ? (
          <Grid item xs={2}>
            <Tooltip
              title={
                filtersApplied
                  ? `${activeFilterCount} active filter${activeFilterCount > 1 ? 's' : ''}`
                  : 'Filters'
              }
            >
              <Badge
                color="primary"
                badgeContent={activeFilterCount}
                invisible={!filtersApplied}
                overlap="circular"
              >
                <IconButton
                  size="small"
                  onClick={handleFilterDialogOpen}
                  sx={{ p: isSmallScreen ? 0.5 : 0.75 }}
                >
                  <FilterListIcon size={isSmallScreen ? 18 : 20} />
                </IconButton>
              </Badge>
            </Tooltip>
          </Grid>
        ) : (
          <>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Competency</InputLabel>
                <Select
                  value={competencyFilter}
                  label="Competency"
                  onChange={handleCompetencyChange}
                  sx={{
                    height: isSmallScreen ? 32 : 36,
                    fontSize: getFontSize('0.75rem', '0.8125rem', '0.875rem')
                  }}
                  endAdornment={
                    competencyFilter && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCompetencyFilter('');
                          if (!isSmallScreen) {
                            setTimeout(() => handleSearchClick(), 50);
                          }
                        }}
                        sx={{
                          position: 'absolute',
                          right: 32,
                          p: 0.25,
                          zIndex: 1
                        }}
                      >
                        <CancelIcon size={16} />
                      </IconButton>
                    )
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="competent">Competent</MenuItem>
                  <MenuItem value="incompetent">Incompetent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={handleStatusChange}
                  sx={{
                    height: isSmallScreen ? 32 : 36,
                    fontSize: getFontSize('0.75rem', '0.8125rem', '0.875rem')
                  }}
                  endAdornment={
                    statusFilter && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setStatusFilter('');
                          if (!isSmallScreen) {
                            setTimeout(() => handleSearchClick(), 50);
                          }
                        }}
                        sx={{
                          position: 'absolute',
                          right: 32,
                          p: 0.25,
                          zIndex: 1
                        }}
                      >
                        <CancelIcon size={16} />
                      </IconButton>
                    )
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                  <MenuItem value="Removed">Removed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Job Type</InputLabel>
                <Select
                  value={jobTypeFilter}
                  label="Job Type"
                  onChange={handleJobTypeChange}
                  sx={{
                    height: isSmallScreen ? 32 : 36,
                    fontSize: getFontSize('0.75rem', '0.8125rem', '0.875rem')
                  }}
                  endAdornment={
                    jobTypeFilter && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setJobTypeFilter('');
                          if (!isSmallScreen) {
                            setTimeout(() => handleSearchClick(), 50);
                          }
                        }}
                        sx={{
                          position: 'absolute',
                          right: 32,
                          p: 0.25,
                          zIndex: 1
                        }}
                      >
                        <CancelIcon size={16} />
                      </IconButton>
                    )
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Full time">Full Time</MenuItem>
                  <MenuItem value="Part time">Part Time</MenuItem>
                  <MenuItem value="Casual">Casual</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );

  const renderFilterDialog = () => (
    <Dialog
      open={filterDialogOpen}
      onClose={handleFilterDialogClose}
      fullWidth
      maxWidth="xs"
      TransitionComponent={Transition}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Filter Options</Typography>
          {filtersApplied && (
            <Button
              color="primary"
              size="small"
              onClick={handleResetFilters}
              sx={{ textTransform: 'none' }}
            >
              Clear all
            </Button>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={handleStatusChange}
              endAdornment={
                statusFilter && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setStatusFilter('');
                    }}
                    sx={{
                      position: 'absolute',
                      right: 32,
                      p: 0.25,
                      zIndex: 1
                    }}
                  >
                    <CancelIcon size={16} />
                  </IconButton>
                )
              }
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
              <MenuItem value="Removed">Removed</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ mb: 1 }}>
            <InputLabel>Competency</InputLabel>
            <Select
              value={competencyFilter}
              label="Competency"
              onChange={handleCompetencyChange}
              endAdornment={
                competencyFilter && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCompetencyFilter('');
                    }}
                    sx={{
                      position: 'absolute',
                      right: 32,
                      p: 0.25,
                      zIndex: 1
                    }}
                  >
                    <CancelIcon size={16} />
                  </IconButton>
                )
              }
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="competent">Competent</MenuItem>
              <MenuItem value="incompetent">Incompetent</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Job Type</InputLabel>
            <Select
              value={jobTypeFilter}
              label="Job Type"
              onChange={handleJobTypeChange}
              endAdornment={
                jobTypeFilter && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setJobTypeFilter('');
                    }}
                    sx={{
                      position: 'absolute',
                      right: 32,
                      p: 0.25,
                      zIndex: 1
                    }}
                  >
                    <CancelIcon size={16} />
                  </IconButton>
                )
              }
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Full time">Full time</MenuItem>
              <MenuItem value="Part time">Part time</MenuItem>
              <MenuItem value="casual">Casual</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleFilterDialogClose}>Cancel</Button>
        <Button onClick={handleApplyFilters} variant="contained" disabled={!filtersApplied}>
          Apply Filters
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderDeleteConfirmDialog = () => (
    <Dialog
      open={deleteConfirmOpen}
      onClose={handleCancelDelete}
      fullWidth
      maxWidth="xs"
      TransitionComponent={Transition}
    >
      <DialogTitle>
        <Typography variant="h6">Confirm Deletion</Typography>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to delete <strong>{activeScreening?.employeeName}</strong>? This
          action cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancelDelete} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirmDelete}
          variant="contained"
          color="error"
          sx={{ textTransform: 'none' }}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderRestoreConfirmDialog = () => (
    <Dialog
      open={restoreConfirmOpen}
      onClose={handleCancelRestore}
      fullWidth
      maxWidth="xs"
      TransitionComponent={Transition}
    >
      <DialogTitle>
        <Typography variant="h6">Confirm Restore</Typography>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to restore <strong>{activeScreening?.employeeName}</strong>? This
          will make the employee active again.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancelRestore} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirmRestore}
          variant="contained"
          color="success"
          sx={{ textTransform: 'none' }}
        >
          Restore
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderTableContent = () => {
    if (loadingScreenings && screenings?.length === 0) {
      return (
        <Card sx={{ boxShadow: 1, borderRadius: 1, overflow: 'auto', width: '100%' }}>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 2 }}>
                Loading people...
              </Typography>
            </Box>
          </CardContent>
        </Card>
      );
    }

    if (!loadingScreenings && screenings?.length === 0) {
      return (
        <Card sx={{ boxShadow: 1, borderRadius: 1, overflow: 'auto', width: '100%' }}>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No people found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {search || statusFilter || competencyFilter || jobTypeFilter
                  ? 'Try adjusting your filters to see more results'
                  : 'No employees have been added yet'}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card sx={{ boxShadow: 1, borderRadius: 1, overflow: 'auto', width: '100%' }}>
        <CardContent sx={{ p: 0 }}>
          <Table sx={{ minWidth: isSmallScreen ? 500 : 650 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f0f2f5' }}>
                <TableCell sx={{ minWidth: 200, py: 1, px: 1, fontWeight: 'bold' }}>
                  Employee
                </TableCell>
                <TableCell sx={{ minWidth: 120, py: 1, px: 1, fontWeight: 'bold' }}>
                  Competency
                </TableCell>
                <TableCell sx={{ minWidth: 120, py: 1, px: 1, fontWeight: 'bold' }}>
                  Status
                </TableCell>
                <TableCell sx={{ minWidth: 120, py: 1, px: 1, fontWeight: 'bold' }}>
                  Job Type
                </TableCell>
                <TableCell sx={{ minWidth: 80, py: 1, px: 1, fontWeight: 'bold' }} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {screenings?.map((screening: Screening) => {
                const status = getStatusDisplay(screening);
                const IconComponent = statusMap[status]?.icon || HelpOutlineIcon;
                const avatarUrl = avatarUrls[screening.employeeId] || null;

                return (
                  <TableRow
                    key={`${screening.employeeId}-${screening.email}`}
                    hover
                    onClick={() => {
                      setSelectedEmployee(screening);
                      setSummaryModalOpen(true);
                    }}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell sx={{ py: 1, px: 1, whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                          src={avatarUrl || undefined}
                          alt={screening.employeeName}
                          sx={{ width: 40, height: 40, mr: 1.5 }}
                        >
                          {screening.employeeName.charAt(0)}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography noWrap sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                            {screening.employeeName}
                          </Typography>
                          <Typography noWrap sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            {screening.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 1, px: 1 }}>
                      <Chip
                        label={screening.competency}
                        size="small"
                        color={getCompetencyColor(screening.competency)}
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 1, px: 1 }}>
                      <Chip
                        label={statusMap[status]?.label || status}
                        size="small"
                        icon={<IconComponent size={16} />}
                        variant="outlined"
                        sx={{
                          fontSize: '0.75rem',
                          height: 24,
                          border: `1px solid ${statusMap[status]?.bgcolor || '#9e9e9e'}`,
                          color: statusMap[status]?.bgcolor || '#000',
                          '& .MuiChip-icon': { color: statusMap[status]?.bgcolor || '#000' }
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 1, px: 1 }}>
                      <Chip
                        label={screening?.employmentType || 'N/A'}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1, px: 1 }}>
                      <Tooltip title="Actions">
                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, screening)}>
                          <MoreVertIcon size={20} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  };

  const renderTableView = () => (
    <>
      {renderFilters()}
      {renderFilterDialog()}
      {renderDeleteConfirmDialog()}
      {renderRestoreConfirmDialog()}
      {renderTableContent()}
      {screenings?.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption">Showing {screenings?.length} people</Typography>
          {hasMoreScreenings && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleLoadMore}
              disabled={loadingScreenings}
            >
              {loadingScreenings ? <CircularProgress size={16} /> : 'Load More'}
            </Button>
          )}
        </Box>
      )}
      {loadingScreenings && screenings?.length > 0 && (
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      )}
    </>
  );

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
        People List
      </Typography>

      {screeningsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {screeningsError}
        </Alert>
      )}

      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setShowSuccess(false)}>
          Employee restored successfully!
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!showError}
        autoHideDuration={5000}
        onClose={() => setShowError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setShowError(null)}>
          {showError || 'Failed to restore employee'}
        </Alert>
      </Snackbar>

      {renderTableView()}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {activeScreening && isRecordDeleted(activeScreening) ? (
          <>
            <MenuItem onClick={handleViewDetails}>
              <Box display="flex" alignItems="center" gap={1}>
                <MdSummarize size={18} />
                Employee Summary
              </Box>
            </MenuItem>
            {isAdminLogin && (
              <MenuItem onClick={handleRestoreScreening} sx={{ color: '#4caf50' }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <RestoreIcon size={18} />
                  Restore
                </Box>
              </MenuItem>
            )}
          </>
        ) : (
          <>
            <MenuItem onClick={handleViewDetails}>
              <Box display="flex" alignItems="center" gap={1}>
                <MdSummarize size={18} />
                Employee Summary
              </Box>
            </MenuItem>
            <MenuItem onClick={handleEditScreening}>
              <Box display="flex" alignItems="center" gap={1}>
                <MdEdit size={18} />
                Employee Details
              </Box>
            </MenuItem>
            <MenuItem onClick={handleRemoveScreening} sx={{ color: '#d32f2f' }}>
              <Box display="flex" alignItems="center" gap={1}>
                <MdDelete size={18} />
                Remove
              </Box>
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  );
};

export default PeopleList;
